import { useEffect, useRef, useState } from "react";
import { Globe, prefersReducedMotion, readTheme, type LatLon } from "@app/globe/globe.ts";
import { useTheme } from "@app/state/theme.ts";

export interface FlyToTarget {
  lat: number;
  lon: number;
  zoom: number;
  offsetY?: number;
  key: number;
}

interface Props {
  interactive?: boolean;
  guess?: LatLon | null;
  answer?: LatLon | null;
  flyTo?: FlyToTarget | null;
  spin?: boolean;
  fill?: number;
  maxDpr?: number;
  ariaLabel?: string;
  onTap?: (point: LatLon, globe: Globe) => void;
  onEnter?: (globe: Globe) => void;
  onReady?: (globe: Globe) => void;
}

export function GlobeCanvas({
  interactive = false,
  guess = null,
  answer = null,
  flyTo = null,
  spin = false,
  fill = 0.44,
  maxDpr,
  ariaLabel,
  onTap,
  onEnter,
  onReady,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const globeRef = useRef<Globe | null>(null);
  const [ready, setReady] = useState(false);
  const tapRef = useRef(onTap);
  const enterRef = useRef(onEnter);
  const readyRef = useRef(onReady);
  tapRef.current = onTap;
  enterRef.current = onEnter;
  readyRef.current = onReady;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const globe = new Globe(canvas, {
      interactive,
      fill,
      maxDpr,
      onTap: (point) => tapRef.current?.(point, globe),
      onEnter: () => enterRef.current?.(globe),
    });
    globeRef.current = globe;
    globe.resize();
    readyRef.current?.(globe);
    setReady(true);
    return () => {
      globe.destroy();
      globeRef.current = null;
    };
    // La instancia se crea una sola vez por montaje.
  }, []);

  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    globe.guess = guess;
    globe.answer = answer;
    globe.draw();
  }, [guess, answer, ready]);

  const resolved = useTheme((s) => s.resolved);
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    globe.theme = readTheme();
    globe.draw();
  }, [resolved, ready]);

  useEffect(() => {
    if (!flyTo) return;
    globeRef.current?.flyTo(flyTo.lat, flyTo.lon, flyTo.zoom, flyTo.offsetY ?? 0);
  }, [flyTo]);

  // Giro decorativo barato: media resolución, ~10 fps, en pausa con la pestaña
  // oculta y parada a los 20 s (dibujar píxel a píxel cuesta en móviles).
  useEffect(() => {
    if (!spin || !ready || prefersReducedMotion()) return;
    const globe = globeRef.current;
    if (!globe) return;
    const started = performance.now();
    let timer = 0;
    const step = () => {
      if (performance.now() - started > 20_000) {
        globe.low = false;
        globe.draw();
        return;
      }
      if (!document.hidden) {
        globe.low = true;
        globe.lon0 += 1.2;
        globe.draw();
      }
      timer = window.setTimeout(step, 100);
    };
    // Primero un dibujo nítido; el giro empieza cuando la página ya es interactiva.
    timer = window.setTimeout(step, 1500);
    return () => {
      window.clearTimeout(timer);
      globe.low = false;
    };
  }, [spin, ready]);

  return (
    <canvas
      ref={canvasRef}
      tabIndex={interactive ? 0 : -1}
      aria-label={ariaLabel}
      aria-hidden={interactive ? undefined : true}
    />
  );
}
